import { AnyAction } from 'redux';
import {
  Adresse,
  Anrede,
  EinreichenResponseModel,
  EinsatzgebieteTier,
  InitialHealthStatusEntry,
  KennzeichnungsArt,
  Tierart,
  Verwendungszweck,
  Wechselgrund,
} from '@ps20/bff-models';
import {
  App,
  PersonensucheModal,
  SearchlistItem,
  UploadedFile,
} from '@ps20-frontend/frontend-models';
import {
  changeSearchDialogState,
  SearchDialogContainerStateChangeAction,
} from '@ps20-frontend/gui-components';
import { AngebotsId, SavedState } from '@ps20-frontend/shop-integration-redux';
import { Injectable } from '@angular/core';
import { BankdatenPayload } from './action-types/bankdaten';
import {
  UnfAddBezugsberechtigterPayload,
  UnfBezugsrechtChangedPayload,
  UnfDeleteBezugsberechtigterPayload,
  UnfDynamikChangedPayload,
  UnfEditBezugsberechtigterPayload,
  UnfIsSofortschutzBesprochenPayload,
  UnfKisBeeintraechtigungenPayload,
  UnfKisFrageJaNeinChanged,
  UnfKisUnterlageneChangedPayload,
  UnfPflegeFrageChangedPayload,
} from './action-types/unfAngebotPayloads';
import { Dokument } from './model/dokumente.enum';
import { VorversicherungDeleteItem } from './action-types/vorversicherungDeleteItem';
import { Vorversicherer } from './data/vorversicherung';
import { DruckstatusEnum } from './reducer/druckstatus.reducer';
import { Kontoinhaber } from './reducer/zahlung.reducer';
import { VermieteteWohnung } from './reducer/vermietete-wohnungen.reducer';
import { VermieteteWohnungChanged } from './action-types/vermieteteWohnungChanged';
import { ImmobilieUebernommen } from './action-types/immobilieUebernommen';
import {
  EinbruchMeldeanlage,
  MechanischeSicherungen,
  Modeaccessoires,
  SG_ADRESSE,
  TresorEintrag,
  Wertsachen,
} from './model';
import { SofortschutzActiveReceived } from './action-types/sofortschutzActiveReceived';
import { VorversicherungsListeGeaendert } from './action-types/vorversicherungsListeGeaendert';
import { VorversicherungsListeVersicherteGefahrenAendern } from './action-types/vorversicherungsListeVersicherteGefahrenAendern';
import { ZielmarktProduktChecked } from './action-types/zielmarktProduktChecked';
import { ZielmarktBegruendungChanged } from './action-types/zielmarktBegruendungChanged';
import { KommdatenChanged } from './action-types/kommdatenChanged';
import { PferdKennzeichnung, ThhPferdAngabenElement } from './reducer/thhPferdAngaben.reducer';
import { PlzChangedActionPayload } from './action-types/plzChangedActionPayload';
import { ThhPferdAngabeChangedPayload } from './action-types/thhPferdAngabeChangedPayload';
import { ThhHundAngabeChangedPayload } from './action-types/thhHundAngabeChangedPayload';
import { HundKennzeichnung, ThhHundAngabenElement } from './reducer/thhHundAngaben.reducer';
import {
  AssekuranzTarif,
  Berater,
  Beratungsdatum,
  Besonderheiten,
  BesonderheitenBesprochen,
  ExtremewetterschutzBesonderheiten,
} from './action-types/beratung';
import {
  SicherungsglaeubigerAdressArtChanged,
  SicherungsglaeubigerAktenzeichenChanged,
  SicherungsglaeubigerBanknameChanged,
  SicherungsglaeubigerHNrChanged,
  SicherungsglaeubigerIndex,
  SicherungsglaeubigerOrtChanged,
  SicherungsglaeubigerPLZ,
  SicherungsglaeubigerPostfachChanged,
  SicherungsglaeubigerStrasseChanged,
} from './action-types/sicherungsglaeubiger';
import {
  EmailAVBVersand,
  FandMitVNstatt,
  GerspraechsDatum,
  GesprachspartnerNachname,
  UhrzeitGespraech,
  VornameGespraechspartner,
} from './action-types/gerspraech';
import { PLZ } from './action-types/abweichenderKontoinhaberAZAM';
import { SicherungsglaeubigerVorhandenChanged } from './action-types/sicherungsglaeubigerVorhandenChanged';
import { AddSicherungsglaeubiger } from './action-types/addSicherungsglaeubiger';
import { Kennzeichnungen } from './reducer/tkvAngaben.reducer';
import {
  TkvAngabenPayload,
  TkvDiagnoseDeleteItemPayload,
  TkvDiagnosePayload,
  TkvEinsatzgebietPayload,
  TkvKennzeichnungsArtPayload,
  TkvTierarztBesuchtPayload,
} from './action-types/tkvAngabenPayload';
import { PoliceVersandArtEnum } from './model/policeVersandArt';
import { HausHaltWertSachenChanged } from './reducer/produkte.reducer';
import { JagdscheinChanged } from './action-types/jagdscheinChanged';
import { BezugsberechtigterModal } from './reducer/unfAngaben.reducer';
import {
  EinbruchMeldeanlageChangedPayload,
  FileToDeletePayload,
  FragebogenValidationRevealedPayload,
  HaushaltModalPayload,
  MechanischeSicherungenChangedPayload,
  TresorChangedPayload,
} from './action-types/haushaltModalPayloads';
import { FragebogenType } from './model/fragebogenType.enum';
import {
  WartezeitverzichtReferenzvertragChangedPayload,
  WartezeitverzichtSelectedPayload,
} from './action-types/wartezeitverzicht';
import {
  ElementarfragebogenAnalyseChanged,
  ElementarfragebogenFilesUploadedSuccessfully,
  ElementarfragebogenUploadedFilesChange,
} from './action-types/elementarfragebogenAnalyseChanged';
import {
  TkvAngabenV2BooleanPayload,
  TkvAngabenV2DiagnosisDetailsPayload,
  TkvAngabenV2DiagnosisPayload,
  TkvAngabenV2TreatmentStatusPayload,
  TkvAngabenV2StringPayload,
  TkvAngabenV2DiagnosisDescriptionPayload,
  TkvAngabenV2HealthStatusListPayload,
  TkvAngabenV2IdentificationTypePayload,
} from './action-types/tkvAngabenV2Payload';

export interface CheckoutAction<T> extends AnyAction {
  payload?: T;
}

export type PlzChangedAction = CheckoutAction<PlzChangedActionPayload>;

@Injectable()
export class CheckoutActions {
  static readonly ADD_SICHERUNGSGLAEUBIGER = 'ADD_SICHERUNGSGLAEUBIGER';
  static readonly AMTLICHES_KENNZEICHEN_CHANGED = 'AMTLICHES_KENNZEICHEN_CHANGED';
  static readonly ANTRAG_EINREICHEN = 'ANTRAG_EINREICHEN';
  static readonly ANTRAG_RESPONSE_RECEIVED = 'ANTRAG_RESPONSE_RECEIVED';
  static readonly BANKDATEN_FAILED = 'BANKDATEN_FAILED';
  static readonly BANKDATEN_NOT_RECEIVED = 'BANKDATEN_NOT_RECEIVED';
  static readonly BANKDATEN_RECEIVED = 'BANKDATEN_RECEIVED';
  static readonly BERATUNG_ASSEKURANZ_TARIF_CHANGED = 'BERATUNG_ASSEKURANZ_TARIF_CHANGED';
  static readonly BERATUNG_BERATERNAME_CHANGED = 'BERATUNG_BERATERNAME_CHANGED';
  static readonly BERATUNG_BESONDERHEITEN_BESPROCHEN_CHANGED = 'BERATUNG_BESONDERHEITEN_BESPROCHEN_CHANGED';
  static readonly BERATUNG_BESONDERHEITEN_CHANGED = 'BERATUNG_BESONDERHEITEN_CHANGED';
  static readonly EXTREMWETTERSCHUTZ_BESONDERHEITEN_CHANGED = 'EXTREMWETTERSCHUTZ_BESONDERHEITEN_CHANGED';
  static readonly BERATUNGSDATUM_GEAENDERT = 'BERATUNGSDATUM_GEAENDERT';
  static readonly BEIBLATT_DRUCKEN_SUCCESS = 'BEIBLATT_DRUCKEN_SUCCESS';
  static readonly BEIBLATT_DRUCKEN_ERROR = 'BEIBLATT_DRUCKEN_ERROR';
  static readonly CHANGE_KONTOINHABER = 'CHANGE_ZAHLUNGSINFORMATIONEN';
  static readonly CHANGE_POLICE_VERSAND_ART = 'CHANGE_POLICE_VERSAND_ART';
  static readonly DELETE_SICHERUNGSGLAEUBIGER = 'DELETE_SICHERUNGSGLAEUBIGER';
  static readonly VORVERSICHERERLISTE_REQUEST_FAILED = 'VORVERSICHERERLISTE_REQUEST_FAILED';
  static readonly VORVERSICHERERLISTE_REQUEST_SUCCESSFUL = 'VORVERSICHERERLISTE_REQUEST_SUCCESSFUL';
  static readonly VNWECHSELGRUND_REQUEST_FAILED = 'VNWECHSELGRUND_REQUEST_FAILED';
  static readonly VNWECHSELGRUND_REQUEST_SUCCESSFUL = 'VNWECHSELGRUND_REQUEST_SUCCESSFUL';
  static readonly AUSSCHLUSS_REQUEST_SUCCESSFUL = 'AUSSCHLUSS_REQUEST_SUCCESSFUL';
  static readonly AUSSCHLUSS_REQUEST_FAILED = 'AUSSCHLUSS_REQUEST_FAILED';
  static readonly DRUCKEN_LINK_CLICKED = 'DRUCKEN_LINK_CLICKED';
  static readonly EINREICHEN_BUTTON_CLICKED = 'EINREICHEN_BUTTON_CLICKED';
  static readonly EINREICHEN_SIMULIEREN_CHANGED = 'EINREICHEN_SIMULIEREN_CHANGED';
  static readonly TESTDOKUMENTE_DOWNLOAD_CLICKED = 'TESTDOKUMENTE_DOWNLOAD_CLICKED';
  static readonly VALIDIERE_INPUT_COMPLECTION_CARD = 'VALIDIRE_INPUT_COMPLECTION_CARD';
  static readonly EMAIL_AVB_VERSAND_CHANGED = 'EMAIL_AVB_VERSAND_CHANGED';

  static readonly FERNABSATZ_CHANGED = 'FERNABSATZ_CHANGED';

  static readonly CAP_START = 'CAP_START';

  static readonly CAP_ANTRAG_EINREICHEN = 'CAP_ANTRAG_EINREICHEN';
  static readonly ELEMENTARFRAGEBOGEN_ANALYSE_CHANGED = 'ELEMENTARFRAGEBOGEN_ANALYSE_CHANGED';

  static readonly FILE_DELETED = 'FILE_DELETED';
  static readonly FILE_DELETION_FAILED = 'FILE_DELETION_FAILED';
  static readonly FILE_DELETION_REQUESTED = 'FILE_DELETION_REQUESTED';
  static readonly REMOVE_NOT_EXISTING_FILES = 'REMOVE_NOT_EXISTING_FILES';
  static readonly CLOSE_FILE_DELETION_MESSAGES = 'CLOSE_FILE_DELETION_MESSAGES';
  static readonly FILE_MODIFICATION_IN_PROGRESS = 'FILE_MODIFICATION_IN_PROGRESS';
  static readonly FILE_MODIFICATION_FINISHED = 'FILE_MODIFICATION_FINISHED';
  static readonly MARK_FILE_TO_DELETE = 'MARK_FILE_TO_DELETE';
  static readonly CLEANUP_FILES_TO_DELETE = 'CLEANUP_FILES_TO_DELETE';

  static readonly GESPRAECHSPARTNER_NACHNAME_CHANGED = 'GESPRAECHSPARTNER_NACHNAME_CHANGED';
  static readonly GESPRAECHSPARTNER_VORNAME_CHANGED = 'VORNAME_GESPRAECHSPARTNER_CHANGED';
  static readonly GESPRAECH_DATUM_CHANGED = 'GESPRAECH_DATUM_CHANGED';
  static readonly GESPRAECH_FAND_MIT_VNSTATT_CHANGED = 'GESPRAECH_FAND_MIT_VNSTATT_CHANGED';
  static readonly GESPRAECH_UHRZEIT_CHANGED = 'GESPRAECH_UHRZEIT_CHANGED';
  static readonly INFOPAKET_DRUCKEN_ERROR = 'INFOPAKET_DRUCKEN_ERROR';
  static readonly INFOPAKET_DRUCKEN_SUCCESS = 'INFOPAKET_DRUCKEN_SUCCESS';
  static readonly JAGDSCHEIN_CHANGED = 'JAGDSCHEIN_CHANGED';
  static readonly JAGDBEHOERDE_NAME_CHANGED = 'JAGDBEHOERDE_NAME_CHANGED';
  static readonly JAGDBEHOERDE_STRASSE_CHANGED = 'JAGDBEHOERDE_STRASSE_CHANGED';
  static readonly JAGDBEHOERDE_PLZ_CHANGED = 'JAGDBEHOERDE_PLZ_CHANGED';
  static readonly JAGDBEHOERDE_ORT_CHANGED = 'JAGDBEHOERDE_ORT_CHANGED';
  static readonly JAGDBEHOERDE_HAUSNUMMER_CHANGED = 'JAGDBEHOERDE_HAUSNUMMER_CHANGED';
  static readonly KOMMUNIKATIONSDATEN_ANLEGEN_REQUESTED = 'KOMMUNIKATIONSDATEN_ANLEGEN_REQUESTED';
  static readonly MAKLERMANDAT_VORHANDEN_CHANGED = 'MAKLERMANDAT_VORHANDEN_CHANGED';
  static readonly MELDEBEHOERDE_VORHANDEN_CHANGED = 'MELDEBEHOERDE_VORHANDEN_CHANGED';
  static readonly MELDEBEHOERDE_AKTENZEICHEN_VORHANDEN_CHANGED =
    'MELDEBEHOERDE_AKTENZEICHEN_VORHANDEN_CHANGED';
  static readonly MELDEBEHOERDE_NAME_CHANGED = 'MELDEBEHOERDE_NAME_CHANGED';
  static readonly MELDEBEHOERDE_AKTENZEICHEN_CHANGED = 'MELDEBEHOERDE_AKTENZEICHEN_CHANGED';
  static readonly MELDEBEHOERDE_STRASSE_CHANGED = 'MELDEBEHOERDE_STRASSE_CHANGED';
  static readonly MELDEBEHOERDE_HAUSNUMMER_CHANGED = 'MELDEBEHOERDE_HAUSNUMMER_CHANGED';
  static readonly MELDEBEHOERDE_PLZ_CHANGED = 'MELDEBEHOERDE_PLZ_CHANGED';
  static readonly MELDEBEHOERDE_ORT_CHANGED = 'MELDEBEHOERDE_ORT_CHANGED';
  static readonly MELDEBEHOERDE_SICHERUNGSDATUM_CHANGED = 'MELDEBEHOERDE_SICHERUNGSDATUM_CHANGED';
  static readonly PKB_DRUCKEN_SUCCESS = 'PKB_DRUCKEN_SUCCESS';
  static readonly PKB_DRUCKEN_ERROR = 'PKB_DRUCKEN_ERROR';
  static readonly WEWE_DRUCKEN_SUCCESS = 'WEWE_DRUCKEN_SUCCESS';
  static readonly WEWE_DRUCKEN_ERROR = 'WEWE_DRUCKEN_ERROR';
  static readonly JAGDBESTAETIGUNG_DRUCKEN_SUCCESS = 'JAGDBESTAETIGUNG_DRUCKEN_SUCCESS';
  static readonly JAGDBESTAETIGUNG_DRUCKEN_ERROR = 'JAGDBESTAETIGUNG_DRUCKEN_ERROR';
  static readonly SAMMELANTRAGSNUMMER_GENERATED = 'SAMMELANTRAGSNUMMER_GENERATED';
  static readonly SAMMELANTRAGSNUMMER_REQUESTED = 'SAMMELANTRAGSNUMMER_REQUESTED';
  static readonly SG_AKTENZEICHEN_CHANGED = 'SG_AKTENZEICHEN_CHANGED';
  static readonly SG_BANKNAME_CHANGED = 'SG_BANKNAME_CHANGED';
  static readonly SG_HAUSNUMMER_CHANGED = 'SG_HAUSNUMMER_CHANGED';
  static readonly SG_ORT_CHANGED = 'SG_ORT_CHANGED';
  static readonly SG_PLZ_CHANGED = 'SG_PLZ_CHANGED';
  static readonly SG_POSTFACH_CHANGED = 'SG_POSTFACH_CHANGED';
  static readonly SG_STRASSE_CHANGED = 'SG_STRASSE_CHANGED';
  static readonly SICHERUNGSGLAEUBIGER_VORHANDEN_CHANGED = 'SICHERUNGSGLAEUBIGER_VORHANDEN_CHANGED';
  static readonly SG_ADRESSART_CHANGED = 'SG_ADRESSART_CHANGED';
  static readonly SOFORTSCHUTZ_ACTIVE_RECEIVED = 'SOFORTSCHUTZ_ACTIVE_RECEIVED';
  static readonly VALIDIEREPLZ_SERVICE_DOWN = 'VALIDIEREPLZ_SERVICE_DOWN';
  static readonly VERMITTLER_FOM_CHANGED = 'VERMITTLER_FOM_CHANGED';
  static readonly VERMITTLER_FREMDVORGANGSNUMMER_CHANGED = 'VERMITTLER_FREMDVORGANGSNUMMER_CHANGED';
  static readonly VERMITTLER_FOM_RESPONSE_MAPPED = 'VERMITTLER_FOM_RESPONSE_MAPPED';
  static readonly VERSANDART_AVB_CHANGED = 'VERSANDART_AVB_CHANGED';
  static readonly VERSICHERUNGSNEHMER_ANREDE_CHANGED = 'VERSICHERUNGSNEHMER_ANREDE_CHANGED';
  static readonly VERSICHERUNGSNEHMER_EMAIL_ANGABENSTATUS_CHANGED =
    'VERSICHERUNGSNEHMER_EMAIL_ANGABENSTATUS_CHANGED';
  static readonly VERSICHERUNGSNEHMER_EMAIL_CHANGED = 'VERSICHERUNGSNEHMER_EMAIL_CHANGED';
  static readonly VERSICHERUNGSNEHMER_GEBURTSDATUM_CHANGED = 'VERSICHERUNGSNEHMER_GEBURTSDATUM_CHANGED';
  static readonly VERSICHERUNGSNEHMER_HAUSNUMMER_CHANGED = 'VERSICHERUNGSNEHMER_HAUSNUMMER_CHANGED';
  static readonly VERSICHERUNGSNEHMER_MOBIL_CHANGED = 'VERSICHERUNGSNEHMER_MOBIL_CHANGED';
  static readonly VERSICHERUNGSNEHMER_MOBIL_ANGABENSTATUS_CHANGED =
    'VERSICHERUNGSNEHMER_MOBIL_ANGABENSTATUS_CHANGED';
  static readonly VERSICHERUNGSNEHMER_NACHNAME_CHANGED = 'VERSICHERUNGSNEHMER_NACHNAME_CHANGED';
  static readonly VERSICHERUNGSNEHMER_FIRMENNAME_CHANGED = 'VERSICHERUNGSNEHMER_FIRMENNAME_CHANGED';
  static readonly VERSICHERUNGSNEHMER_ORT_CHANGED = 'VERSICHERUNGSNEHMER_ORT_CHANGED';
  static readonly VERSICHERUNGSNEHMER_PLZ_CHANGED = 'VERSICHERUNGSNEHMER_PLZ_CHANGED';
  static readonly VERSICHERUNGSNEHMER_PLZ_INVALID = 'VERSICHERUNGSNEHMER_PLZ_INVALID';
  static readonly VERSICHERUNGSNEHMER_PLZ_VALID = 'VERSICHERUNGSNEHMER_PLZ_VALID';
  static readonly VERSICHERUNGSNEHMER_STRASSE_CHANGED = 'VERSICHERUNGSNEHMER_STRASSE_CHANGED';
  static readonly VERSICHERUNGSNEHMER_TELEFON_CHANGED = 'VERSICHERUNGSNEHMER_TELEFON_CHANGED';
  static readonly VERSICHERUNGSNEHMER_VORNAME_CHANGED = 'VERSICHERUNGSNEHMER_VORNAME_CHANGED';
  static readonly VERTRAGSSCHLUSSVERFAHREN_CHANGED = 'VERTRAGSSCHLUSSVERFAHREN_CHANGED';
  static readonly VORVERSICHERUNG_VERSICHERUNGSLISTE_ADD_ITEM =
    'VORVERSICHERUNG_VERSICHERUNGSLISTE_ADD_ITEM';
  static readonly VORVERSICHERUNG_VERSICHERUNGSLISTE_DELETE_ITEM =
    'VORVERSICHERUNG_VERSICHERUNGSLISTE_DELETE_ITEM';
  static readonly VORVERSICHERUNG_VERSICHERUNGSLISTE_CHANGE_ITEM =
    'VORVERSICHERUNG_VERSICHERUNGSLISTE_CHANGE_ITEM';
  static readonly VORVERSICHERUNG_VERSICHERUNGSLISTE_VERSICHERTEGEFAHREN_GEAENDERT =
    'VORVERSICHERUNG_VERSICHERUNGSLISTE_VERSICHERTEGEFAHREN_GEAENDERT';
  static readonly VORVERSICHERERLISTE_RETRIEVED = 'VORVERSICHERERLISTE_RETRIEVED';
  static readonly WERTSACHEN_CHANGED = 'WERTSACHEN_CHANGED';
  static readonly ZAHLUNG_BIC_CHANGED = 'ZAHLUNG_BIC_CHANGED';
  static readonly ZAHLUNG_IBAN_CHANGED = 'ZAHLUNG_IBAN_CHANGED';
  static readonly ZAHLUNG_IBAN_VALIDATED = 'ZAHLUNG_IBAN_VALIDATED';
  static readonly ZAHLUNG_MANUELLE_IBAN_EINGABE = 'ZAHLUNG_MANUELLE_IBAN_EINGABE';
  static readonly ZAHLUNG_VERWENDUNGSZWECK_CHANGED = 'ZAHLUNG_VERWENDUNGSZWECK_CHANGED';
  static readonly ZIELMARKT_CHANGED = 'ZIELMARKT_CHANGED';
  static readonly ZIELMARKT_PRODUKT_CHECKED = 'ZIELMARKT_PRODUKT_CHECKED';
  static readonly ZIELMARKT_BEGRUENDUNG_CHANGED = 'ZIELMARKT_BEGRUENDUNG_CHANGED';
  static readonly VERMIETETE_WOHNUNG_CHANGED = 'VERMIETETE_WOHNUNG_CHANGED';
  static readonly OPEN_IMMOBILIEN_MODAL = 'OPEN_IMMOBILIEN_MODAL';
  static readonly CLOSE_IMMOBILIEN_MODAL = 'CLOSE_IMMOBILIEN_MODAL';
  static readonly IMMOBILIE_UEBERNOMMEN = 'IMMOBILIE_UEBERNOMMEN';
  static readonly WARTEZEITVERZICHT_SELECTED = 'WARTEZEITVERZICHT_SELECTED';
  static readonly WARTEZEITVERZICHT_REFERENZVERTRAG_CHANGED = 'WARTEZEITVERZICHT_REFERENZVERTRAG_CHANGED';
  static readonly ZURUECK_ZUM_SHOP_MODAL_OPEN = 'ZURUECK_ZUM_SHOP_MODAL_OPEN';
  static readonly ZURUECK_ZUM_SHOP_MODAL_CLOSE = 'ZURUECK_ZUM_SHOP_MODAL_CLOSE';
  static readonly THH_PFERD_ANGABE_CHANGED = 'THH_PFERD_ANGABE_CHANGED';
  static readonly THH_PFERD_KENNZEICHENART_CHANGED = 'THH_PFERD_KENNZEICHENART_CHANGED';
  static readonly THH_HUND_ANGABE_CHANGED = 'THH_HUND_ANGABE_CHANGED';
  static readonly THH_HUND_KENNZEICHENART_CHANGED = 'THH_HUND_KENNZEICHENART_CHANGED';
  static readonly STAATSBUERGERSCHAFTEN_REQUESTED = 'STAATSBUERGERSCHAFTEN_REQUESTED';
  static readonly STAATSBUERGERSCHAFTEN_RECEIVED = 'STAATSBUERGERSCHAFTEN_RECEIVED';
  static readonly TIERARZT_BESUCHT_CHANGED = 'TIERARZT_BESUCHT_CHANGED';
  static readonly DIAGNOSE_LIST_RECEIVED = 'DIAGNOSE_LIST_RECEIVED';
  static readonly DIAGNOSE_DETAILS_LIST_RECEIVED = 'DIAGNOSE_DETAILS_LIST_RECEIVED';
  static readonly DIAGNOSE_CHANGED = 'DIAGNOSE_CHANGED';
  static readonly DIAGNOSE_DETAIL_CHANGED = 'DIAGNOSE_DETAIL_CHANGED';
  static readonly DIAGNOSE_BESCHREIBUNG_CHANGED = 'DIAGNOSE_BESCHREIBUNG_CHANGED';
  static readonly DIAGNOSE_ITEM_DELETE = 'DIAGNOSE_ITEM_DELETE';
  static readonly DIAGNOSE_ITEM_ADD = 'DIAGNOSE_ITEM_ADD';

  static readonly TKV_TIERNAME_CHANGED = 'TKV_TIERNAME_CHANGED';
  static readonly TKV_EINSATZGEBIET_CHANGED = 'TKV_EINSATZGEBIET_CHANGED';
  static readonly TKV_KENNZEICHNUNGSART_CHANGED = 'TKV_KENNZEICHNUNGSART_CHANGED';
  static readonly TKV_KENNZEICHNUNG_CHANGED = 'TKV_KENNZEICHNUNG_CHANGED';

  static readonly TKV_V2_ASSISTANCE_DOG_CHANGED = 'TKV_V2_ASSISTANCE_DOG_CHANGED';
  static readonly TKV_V2_HEALTH_STATUS_LIST_DOG_CAT_RECEIVED = 'TKV_V2_HEALTH_STATUS_LIST_DOG_CAT_RECEIVED';
  static readonly TKV_V2_HEALTH_STATUS_LIST_HORSE_RECEIVED = 'TKV_V2_HEALTH_STATUS_LIST_HORSE_RECEIVED';
  static readonly TKV_V2_HEALTH_STATUS_ENTRY_ADDED = 'TKV_V2_HEALTH_STATUS_ENTRY_ADDED';
  static readonly TKV_V2_HEALTH_STATUS_ENTRY_REMOVED = 'TKV_V2_HEALTH_STATUS_ENTRY_REMOVED';
  static readonly TKV_V2_DIAGNOSIS_CHANGED = 'TKV_V2_DIAGNOSIS_CHANGED';
  static readonly TKV_V2_DIAGNOSIS_DETAILS_CHANGED = 'TKV_V2_DIAGNOSIS_DETAILS_CHANGED';
  static readonly TKV_V2_DIAGNOSIS_DESCRIPTION_CHANGED = 'TKV_V2_DIAGNOSIS_DESCRIPTION_CHANGED';
  static readonly TKV_V2_TREATMENT_STATUS_CHANGED = 'TKV_V2_TREATMENT_STATUS_CHANGED';
  static readonly TKV_V2_IDENTIFICATION_TYPE_CHANGED = 'TKV_V2_IDENTIFICATION_TYPE_CHANGED';
  static readonly TKV_V2_IDENTIFICATION_CHANGED = 'TKV_V2_IDENTIFICATION_CHANGED';

  static readonly CHANGE_AZAM_ABWEICHENDER_KONTOINHABER = 'CHANGE_AZAM_ABWEICHENDER_KONTOINHABER';
  static readonly RESET_AZAM_KONTOINHABER = 'RESET_AZAM_KONTOINHABER';
  static readonly RESET_AZAM_BEZUGSBERECHTIGTEN_MODAL = 'RESET_AZAM_BEZUGSBERECHTIGTEN_MODAL';
  static readonly AZAM_ABWEICHENDER_KONTOINHABER_PLZ_CHANGED = 'AZAM_ABWEICHENDER_KONTOINHABER_PLZ_CHANGED';

  static readonly AZAM_BEZUGSBERECHTIGTER_PLZ_CHANGED = 'AZAM_BEZUGSBERECHTIGTER_PLZ_CHANGED';
  static readonly ADOPT_CURRENT_KONTOINHABER_TO_AZAM_ABWEICHENDER_KONTOINAHBER =
    'ADOPT_CURRENT_KONTOINHABER_TO_AZAM_ABWEICHENDER_KONTOINAHBER';
  static readonly AZAM_PARTIAL_KONTOINHABER_UPDATE = 'AZAM_PARTIAL_KONTOINHABER_UPDATE';

  static readonly AZAM_PARTIAL_BEZUGSBERECHTIGER_UPDATE = 'AZAM_PARTIAL_BEZUGSBERECHTIGER_UPDATE';
  static readonly AZAM_PARTIAL_KONTOINHABER_ADDRESS_UPDATE = 'AZAM_PARTIAL_KONTOINHABER_ADDRESS_UPDATE';

  static readonly AZAM_PARTIAL_BEZUGSBERECHTIGTER_ADDRESS_UPDATE =
    'AZAM_PARTIAL_BEZUGSBERECHTIGTER_ADDRESS_UPDATE';
  static readonly SORT_PRODUKTE = 'SORT_PRODUKTE';

  static readonly PROGRAMMTEILNAHME_SUCCESS = 'PROGRAMMTEILNAHME_SUCCESS';
  static readonly PROGRAMMTEILNAHME_FAILED = 'PROGRAMMTEILNAHME_FAILED';

  static readonly VN_WECHSELGRUND_LIST_RECEIVED = 'VN_WECHSELGRUND_LIST_RECEIVED';
  static readonly AUSSCHLUSS_LIST_RECEIVED = 'AUSSCHLUSS_LIST_RECEIVED';

  static readonly UNF_DYNAMIK_CHANGED = 'UNF_DYNAMIK_CHANGED';
  static readonly UNF_BEZUGSRECHT_CHANGED = 'UNF_BEZUGSRECHT_CHANGED';
  static readonly UNF_KIS_FRAGE_CHANGED = 'UNF_KIS_FRAGE_CHANGED';
  static readonly UNF_KIS_BEEINTRAECHTIGUNGEN_CHANGED = 'UNF_KIS_BEEINTRAECHTIGUNGEN_CHANGED';
  static readonly UNF_IS_SOFORTSCHUTZ_BESPROCHEN_CHANGED = 'UNF_IS_SOFORTSCHUTZ_BESPROCHEN_CHANGED';
  static readonly UNF_KIS_UNTERLAGENEINGEREICHT_CHANGED = 'UNF_KIS_UNTERLAGENEINGEREICHT_CHANGED';
  static readonly UNF_DATENSCHUTZEINWILLIGUNG_CHANGED = 'UNF_DATENSCHUTZEINWILLIGUNG_CHANGED';
  static readonly UNF_PFLEGEFRAGE_CHANGED = 'UNF_PFLEGEFRAGE_CHANGED';
  static readonly UNF_UPDATE_GESUNDHEITSDATEN_AFTER_LOAD = 'UNF_UPDATE_GESUNDHEITSDATEN_AFTER_LOAD';
  static readonly UNF_ADD_BEZUGSBERECHTIGTER = 'UNF_ADD_BEZUGSBERECHTIGTER';
  static readonly UNF_EDIT_BEZUGSBERECHTIGTER = 'UNF_EDIT_BEZUGSBERECHTIGTER';
  static readonly UNF_DELETE_BEZUGSBERECHTIGTER = 'UNF_DELETE_BEZUGSBERECHTIGTER';

  static readonly VHV_WERTSACHEN_SUMMEN_CHANGED = 'VHV_WERTSACHEN_SUMMEN_CHANGED';
  static readonly VHV_MODEACCESSOIRES_CHANGED = 'VHV_MODEACCESSOIRES_CHANGED';
  static readonly EMA_VORHANDEN_CHANGED = 'EMA_VORHANDEN_CHANGED';
  static readonly TRESORE_VORHANDEN_CHANGED = 'TRESORE_VORHANDEN_CHANGED';

  static readonly EINBRUCHMELDEANLAGE_MODAL_OPENED = 'EINBRUCHMELDEANLAGE_MODAL_OPENED';
  static readonly WERTBEHAELTNISSE_MODAL_OPENED = 'WERTBEHAELTNISSE_MODAL_OPENED';
  static readonly MECHANISCHE_SICHERUNGEN_MODAL_OPENED = 'MECHANISCHE_SICHERUNGEN_MODAL_OPENED';
  static readonly FRAGEBOGEN_VALIDATION_REVEALED = 'FRAGEBOGEN_VALIDATION_REVEALED';
  static readonly EMA_CHANGED = 'EMA_CHANGED';
  static readonly TRESOR_CHANGED = 'TRESOR_CHANGED';
  static readonly MECHANISCHE_SICHERUNGEN_CHANGED = 'MECHANISCHE_SICHERUNGEN_CHANGED';
  static readonly ELEMENTARFRAGEBOGEN_UPLOADED_FILES_CHANGED = 'ELEMENTARFRAGEBOGEN_UPLOADED_FILES_CHANGED';
  static readonly ELEMENTARFRAGEBOGEN_FILES_UPLOADED_SUCCESSFULLY =
    'ELEMENTARFRAGEBOGEN_FILES_UPLOADED_SUCCESSFULLY';
  static readonly ELEMENTARFRAGEBOGEN_FILE_DELETED_SUCCESSFULLY =
    'ELEMENTARFRAGEBOGEN_FILE_DELETED_SUCCESSFULLY';
  static readonly REMOVE_ELEMENTARFRAGEBOGEN_OPERATIONS_STATUS =
    'REMOVE_ELEMENTARFRAGEBOGEN_OPERATIONS_STATUS';

  azamKontoinhaberUebernehmen(): AnyAction {
    return {
      type: CheckoutActions.CHANGE_AZAM_ABWEICHENDER_KONTOINHABER,
    };
  }

  adoptKontoinhaberToAzamAbweichenderKontoInhaber(): AnyAction {
    return {
      type: CheckoutActions.ADOPT_CURRENT_KONTOINHABER_TO_AZAM_ABWEICHENDER_KONTOINAHBER,
    };
  }

  azamResetAbweichendenKontoinhaber(): AnyAction {
    return {
      type: CheckoutActions.RESET_AZAM_KONTOINHABER,
    };
  }

  azamResetBezugsberechtigenModal(): AnyAction {
    return {
      type: CheckoutActions.RESET_AZAM_BEZUGSBERECHTIGTEN_MODAL,
    };
  }

  addSicherungsglaeubiger(angebotsId: string): CheckoutAction<AddSicherungsglaeubiger> {
    return {
      type: CheckoutActions.ADD_SICHERUNGSGLAEUBIGER,
      payload: { angebotsId },
    };
  }

  amtlichesKennzeichenChanged(kennzeichen: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.AMTLICHES_KENNZEICHEN_CHANGED,
      payload: kennzeichen,
    };
  }

  antragEinreichen(): AnyAction {
    return {
      type: CheckoutActions.ANTRAG_EINREICHEN,
    };
  }

  antragEinreichenCap(): AnyAction {
    return {
      type: CheckoutActions.CAP_ANTRAG_EINREICHEN,
    };
  }

  capStart(): AnyAction {
    return {
      type: CheckoutActions.CAP_START,
    };
  }

  antragResponseReceived(data: EinreichenResponseModel): CheckoutAction<EinreichenResponseModel> {
    return {
      type: CheckoutActions.ANTRAG_RESPONSE_RECEIVED,
      payload: data,
    };
  }

  bankdatenReceived(bankdaten: BankdatenPayload): CheckoutAction<BankdatenPayload> {
    return { type: CheckoutActions.BANKDATEN_RECEIVED, payload: bankdaten };
  }

  bankdatenNotReceived(): AnyAction {
    return { type: CheckoutActions.BANKDATEN_NOT_RECEIVED };
  }

  bankdatenFailed(): AnyAction {
    return { type: CheckoutActions.BANKDATEN_FAILED };
  }

  beraternameChanged(beratername: string): CheckoutAction<Berater> {
    return {
      type: CheckoutActions.BERATUNG_BERATERNAME_CHANGED,
      payload: { beratername },
    };
  }

  besonderheitenBesprochenChanged(
    besonderheitenBesprochen: boolean,
  ): CheckoutAction<BesonderheitenBesprochen> {
    return {
      type: CheckoutActions.BERATUNG_BESONDERHEITEN_BESPROCHEN_CHANGED,
      payload: { besonderheitenBesprochen },
    };
  }

  besonderheitenChanged(besonderheiten: string): CheckoutAction<Besonderheiten> {
    return {
      type: CheckoutActions.BERATUNG_BESONDERHEITEN_CHANGED,
      payload: { besonderheiten },
    };
  }

  extremwetterschutzBesonderheitenChanged(
    extremwetterschutzBesonderheiten: string,
  ): CheckoutAction<ExtremewetterschutzBesonderheiten> {
    return {
      type: CheckoutActions.EXTREMWETTERSCHUTZ_BESONDERHEITEN_CHANGED,
      payload: { extremwetterschutzBesonderheiten },
    };
  }

  beratungsdatumGeaendert(beratungsdatum: string): CheckoutAction<Beratungsdatum> {
    return {
      type: CheckoutActions.BERATUNGSDATUM_GEAENDERT,
      payload: { beratungsdatum },
    };
  }

  changeKontoinhaber(kontoinhaber: Kontoinhaber): CheckoutAction<Kontoinhaber> {
    return {
      type: CheckoutActions.CHANGE_KONTOINHABER,
      payload: kontoinhaber,
    };
  }

  changePoliceVersandform(versandArt: PoliceVersandArtEnum): CheckoutAction<PoliceVersandArtEnum> {
    return {
      type: CheckoutActions.CHANGE_POLICE_VERSAND_ART,
      payload: versandArt,
    };
  }

  deleteSicherungsglaeubiger(
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerIndex> {
    return {
      type: CheckoutActions.DELETE_SICHERUNGSGLAEUBIGER,
      payload: { index, angebotsId },
    };
  }

  druckenLinkClicked(dokument: Dokument): CheckoutAction<Dokument> {
    return {
      type: CheckoutActions.DRUCKEN_LINK_CLICKED,
      payload: dokument,
    };
  }

  infopaketDruckenError(status: DruckstatusEnum): CheckoutAction<DruckstatusEnum> {
    return { type: CheckoutActions.INFOPAKET_DRUCKEN_ERROR, payload: status };
  }

  beiblattDruckenError(status: DruckstatusEnum): CheckoutAction<DruckstatusEnum> {
    return { type: CheckoutActions.BEIBLATT_DRUCKEN_ERROR, payload: status };
  }

  pkbDruckenError(status: DruckstatusEnum): CheckoutAction<DruckstatusEnum> {
    return { type: CheckoutActions.PKB_DRUCKEN_ERROR, payload: status };
  }

  jagdbestaetigungDruckenError(status: DruckstatusEnum): CheckoutAction<DruckstatusEnum> {
    return { type: CheckoutActions.JAGDBESTAETIGUNG_DRUCKEN_ERROR, payload: status };
  }

  einreichenButtonClicked(): AnyAction {
    return {
      type: CheckoutActions.EINREICHEN_BUTTON_CLICKED,
    };
  }

  einreichenSimulierenChanged(einreichenSimulieren: boolean): CheckoutAction<boolean> {
    return {
      type: CheckoutActions.EINREICHEN_SIMULIEREN_CHANGED,
      payload: einreichenSimulieren,
    };
  }

  testdokumenteDownloadClicked(): AnyAction {
    return {
      type: CheckoutActions.TESTDOKUMENTE_DOWNLOAD_CLICKED,
    };
  }

  emailAVBVersandChanged(emailAVBVersand: string): CheckoutAction<EmailAVBVersand> {
    return {
      type: CheckoutActions.EMAIL_AVB_VERSAND_CHANGED,
      payload: { emailAVBVersand },
    };
  }

  gespraechspartnerNachnameChanged(
    nachnameGespraechspartner: string,
  ): CheckoutAction<GesprachspartnerNachname> {
    return {
      type: CheckoutActions.GESPRAECHSPARTNER_NACHNAME_CHANGED,
      payload: { nachnameGespraechspartner },
    };
  }

  gespraechspartnerVornameChanged(
    vornameGespraechspartner: string,
  ): CheckoutAction<VornameGespraechspartner> {
    return {
      type: CheckoutActions.GESPRAECHSPARTNER_VORNAME_CHANGED,
      payload: { vornameGespraechspartner },
    };
  }

  gespraechDatumChanged(datumGespraech: string): CheckoutAction<GerspraechsDatum> {
    return {
      type: CheckoutActions.GESPRAECH_DATUM_CHANGED,
      payload: { datumGespraech },
    };
  }

  gespraechFandMitVNStattChanged(fandMitVNStatt: boolean): CheckoutAction<FandMitVNstatt> {
    return {
      type: CheckoutActions.GESPRAECH_FAND_MIT_VNSTATT_CHANGED,
      payload: { fandMitVNStatt },
    };
  }

  openPersonenSearchDialog(
    personensucheModal: PersonensucheModal,
    angebotsId?: string,
  ): SearchDialogContainerStateChangeAction<SearchlistItem> {
    return changeSearchDialogState({
      open: true,
      loading: false,
      error: false,
      confirmed: false,
      personensucheModal,
      angebotsId,
    });
  }

  gespraechUhrzeitChanged(uhrzeitGespraech: string): CheckoutAction<UhrzeitGespraech> {
    return {
      type: CheckoutActions.GESPRAECH_UHRZEIT_CHANGED,
      payload: { uhrzeitGespraech },
    };
  }

  sgAktenzeichenChanged(
    sgAktenzeichen: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerAktenzeichenChanged> {
    return {
      type: CheckoutActions.SG_AKTENZEICHEN_CHANGED,
      payload: { sgAktenzeichen, index, angebotsId },
    };
  }

  sgBanknameChanged(
    sgBankname: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerBanknameChanged> {
    return {
      type: CheckoutActions.SG_BANKNAME_CHANGED,
      payload: { sgBankname, index, angebotsId },
    };
  }

  sgHausnummerChanged(
    sgHausnummer: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerHNrChanged> {
    return {
      type: CheckoutActions.SG_HAUSNUMMER_CHANGED,
      payload: { sgHausnummer, index, angebotsId },
    };
  }

  sgPlzChanged(
    sgPlz: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerPLZ> {
    return {
      type: CheckoutActions.SG_PLZ_CHANGED,
      payload: { sgPlz, index, angebotsId },
    };
  }

  sgOrtChanged(
    sgOrt: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerOrtChanged> {
    return {
      type: CheckoutActions.SG_ORT_CHANGED,
      payload: { sgOrt, index, angebotsId },
    };
  }

  sgPostfachChanged(
    sgPostfach: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerPostfachChanged> {
    return {
      type: CheckoutActions.SG_POSTFACH_CHANGED,
      payload: { sgPostfach, index, angebotsId },
    };
  }

  sgStrasseChanged(
    sgStrasse: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerStrasseChanged> {
    return {
      type: CheckoutActions.SG_STRASSE_CHANGED,
      payload: { sgStrasse, index, angebotsId },
    };
  }

  sgAdressArtChanged(
    adressArt: SG_ADRESSE,
    index: number,
    angebotsId: string,
  ): CheckoutAction<SicherungsglaeubigerAdressArtChanged> {
    return {
      type: CheckoutActions.SG_ADRESSART_CHANGED,
      payload: { adressArt, index, angebotsId },
    };
  }

  sicherungsglaeubigerVorhandenChanged(
    changed: boolean,
    angebotsId: string,
    index: number,
  ): CheckoutAction<SicherungsglaeubigerVorhandenChanged> {
    return {
      type: CheckoutActions.SICHERUNGSGLAEUBIGER_VORHANDEN_CHANGED,
      payload: { changed, angebotsId, index },
    };
  }

  elementarfragebogenAnalyseChanged(
    changed: boolean,
    angebotsId: string,
    index: number,
  ): CheckoutAction<ElementarfragebogenAnalyseChanged> {
    return {
      type: CheckoutActions.ELEMENTARFRAGEBOGEN_ANALYSE_CHANGED,
      payload: { changed, angebotsId, index },
    };
  }

  sofortschutzActiveReceived(
    sofortschutzActiveReceived: SofortschutzActiveReceived,
  ): CheckoutAction<SofortschutzActiveReceived> {
    return {
      type: CheckoutActions.SOFORTSCHUTZ_ACTIVE_RECEIVED,
      payload: sofortschutzActiveReceived,
    };
  }

  vorversichererListeRetrieved(
    vorversichererListe: Array<Vorversicherer>,
  ): CheckoutAction<Array<Vorversicherer>> {
    return {
      type: CheckoutActions.VORVERSICHERERLISTE_RETRIEVED,
      payload: vorversichererListe,
    };
  }

  vorversicherungsListeItemAendern(
    vorversicherungsListeGeaendert: VorversicherungsListeGeaendert,
  ): CheckoutAction<VorversicherungsListeGeaendert> {
    return {
      type: CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_CHANGE_ITEM,
      payload: vorversicherungsListeGeaendert,
    };
  }

  vorversicherungsListItemLoeschen(
    payload: VorversicherungDeleteItem,
  ): CheckoutAction<VorversicherungDeleteItem> {
    return {
      type: CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_DELETE_ITEM,
      payload,
    };
  }

  diagnoseListItemLoeschen(
    itemIndex: number,
    angebotsId: string,
  ): CheckoutAction<TkvDiagnoseDeleteItemPayload> {
    return {
      type: CheckoutActions.DIAGNOSE_ITEM_DELETE,
      payload: { itemIndex, angebotsId },
    };
  }

  diagnoseListItemAdd(angebotsId: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.DIAGNOSE_ITEM_ADD,
      payload: angebotsId,
    };
  }

  vorversicherungsListeVersicherteGefahrenAendern(
    vorversicherungsListeVersicherteGefahrenAendern: VorversicherungsListeVersicherteGefahrenAendern,
  ): CheckoutAction<VorversicherungsListeVersicherteGefahrenAendern> {
    return {
      type: CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_VERSICHERTEGEFAHREN_GEAENDERT,
      payload: vorversicherungsListeVersicherteGefahrenAendern,
    };
  }

  vorversicherungsListeItemHinzufuegen(
    appId: App,
    angebotsId?: string,
  ): CheckoutAction<AngebotsId> {
    return {
      type: CheckoutActions.VORVERSICHERUNG_VERSICHERUNGSLISTE_ADD_ITEM,
      payload: { appId, angebotsId },
    };
  }

  anredeChanged(anrede: Anrede): AnyAction {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_ANREDE_CHANGED,
      payload: { anrede },
    };
  }

  vornameChanged(vorname: string): CheckoutAction<{ vorname: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_VORNAME_CHANGED,
      payload: { vorname },
    };
  }

  nachnameChanged(nachname: string): CheckoutAction<{ nachname: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_NACHNAME_CHANGED,
      payload: { nachname },
    };
  }

  geburtsdatumChanged(geburtsdatum: string): CheckoutAction<{ geburtsdatum: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_GEBURTSDATUM_CHANGED,
      payload: { geburtsdatum },
    };
  }

  plzChanged(plz: string): PlzChangedAction {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_PLZ_CHANGED,
      payload: { plz },
    };
  }

  ortChanged(ort: string): CheckoutAction<{ ort: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_ORT_CHANGED,
      payload: { ort },
    };
  }

  strasseChanged(strasse: string): CheckoutAction<{ strasse: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_STRASSE_CHANGED,
      payload: { strasse },
    };
  }

  hausnummerChanged(hausnummer: string): CheckoutAction<{ hausnummer: string }> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_HAUSNUMMER_CHANGED,
      payload: { hausnummer },
    };
  }

  emailChanged(email: KommdatenChanged): CheckoutAction<KommdatenChanged> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_EMAIL_CHANGED,
      payload: email,
    };
  }

  emailAngabenstatusChanged(email: KommdatenChanged): CheckoutAction<KommdatenChanged> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_EMAIL_ANGABENSTATUS_CHANGED,
      payload: email,
    };
  }

  mobilChanged(mobil: KommdatenChanged): CheckoutAction<KommdatenChanged> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_MOBIL_CHANGED,
      payload: mobil,
    };
  }

  mobilAngabenstatusChanged(mobil: KommdatenChanged): CheckoutAction<KommdatenChanged> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_MOBIL_ANGABENSTATUS_CHANGED,
      payload: mobil,
    };
  }

  telefonChanged(telefon: KommdatenChanged): CheckoutAction<KommdatenChanged> {
    return {
      type: CheckoutActions.VERSICHERUNGSNEHMER_TELEFON_CHANGED,
      payload: telefon,
    };
  }

  ibanChanged(iban: string): CheckoutAction<{ iban: string }> {
    return {
      type: CheckoutActions.ZAHLUNG_IBAN_CHANGED,
      payload: { iban },
    };
  }

  manuelleIbanEingabe(changed: boolean): CheckoutAction<{ changed: boolean }> {
    return {
      type: CheckoutActions.ZAHLUNG_MANUELLE_IBAN_EINGABE,
      payload: { changed },
    };
  }

  ibanValidated(iban: string, bic?: string): CheckoutAction<{ iban: string; swiftcode: string }> {
    return {
      type: CheckoutActions.ZAHLUNG_IBAN_VALIDATED,
      payload: {
        iban,
        swiftcode: bic,
      },
    };
  }

  verwendungszweckChanged(
    verwendungszweck: Verwendungszweck,
  ): CheckoutAction<{ verwendungszweck: Verwendungszweck }> {
    return {
      type: CheckoutActions.ZAHLUNG_VERWENDUNGSZWECK_CHANGED,
      payload: { verwendungszweck },
    };
  }

  vermittlerFomChanged(fom: string): CheckoutAction<{ fom: string }> {
    return {
      type: CheckoutActions.VERMITTLER_FOM_CHANGED,
      payload: { fom },
    };
  }

  vermittlerFremdvorgangsnummerChanged(
    fremdvorgangsnummer: string,
  ): CheckoutAction<{ fremdvorgangsnummer: string }> {
    return {
      type: CheckoutActions.VERMITTLER_FREMDVORGANGSNUMMER_CHANGED,
      payload: { fremdvorgangsnummer },
    };
  }

  vertragsschlussverfahrenChanged(
    vertragsschlussverfahren: string,
  ): CheckoutAction<{ vertragsschlussverfahren: string }> {
    return {
      type: CheckoutActions.VERTRAGSSCHLUSSVERFAHREN_CHANGED,
      payload: { vertragsschlussverfahren },
    };
  }

  versandArtAVBChanged(versandArtAVB: string): CheckoutAction<{ versandArtAVB: string }> {
    return {
      type: CheckoutActions.VERSANDART_AVB_CHANGED,
      payload: { versandArtAVB },
    };
  }

  wartezeitverzichtSelected(
    value: boolean,
    appId: App,
  ): CheckoutAction<WartezeitverzichtSelectedPayload> {
    return {
      type: CheckoutActions.WARTEZEITVERZICHT_SELECTED,
      payload: { value: value, appId: appId },
    };
  }

  wartezeitverzichtReferenzvertragChanged(
    value: string,
    appId: App,
  ): CheckoutAction<WartezeitverzichtReferenzvertragChangedPayload> {
    return {
      type: CheckoutActions.WARTEZEITVERZICHT_REFERENZVERTRAG_CHANGED,
      payload: { value: value, appId: appId },
    };
  }

  wertsachenChanged(
    haltWertSachenChanged: HausHaltWertSachenChanged,
  ): CheckoutAction<HausHaltWertSachenChanged> {
    return {
      type: CheckoutActions.WERTSACHEN_CHANGED,
      payload: haltWertSachenChanged,
    };
  }

  zahlungBicChanged(bic: string): CheckoutAction<{ bic: string }> {
    return {
      type: CheckoutActions.ZAHLUNG_BIC_CHANGED,
      payload: { bic },
    };
  }

  vermieteteWohnungChanged(
    index: number,
    wohnung: VermieteteWohnung,
  ): CheckoutAction<VermieteteWohnungChanged> {
    return {
      type: CheckoutActions.VERMIETETE_WOHNUNG_CHANGED,
      payload: { index, wohnung },
    };
  }

  openImmobilienModal(): AnyAction {
    return {
      type: CheckoutActions.OPEN_IMMOBILIEN_MODAL,
    };
  }

  closeImmobilienModal(): AnyAction {
    return {
      type: CheckoutActions.CLOSE_IMMOBILIEN_MODAL,
    };
  }

  immobilieUebernommen(index: number, immoId: string): CheckoutAction<ImmobilieUebernommen> {
    return {
      type: CheckoutActions.IMMOBILIE_UEBERNOMMEN,
      payload: { index, immoId },
    };
  }

  zielmarktChanged(
    verkaufInnerhalbZielmarkt: boolean,
  ): CheckoutAction<{ verkaufInnerhalbZielmarkt: boolean }> {
    return {
      type: CheckoutActions.ZIELMARKT_CHANGED,
      payload: { verkaufInnerhalbZielmarkt },
    };
  }

  zielmarktProduktChecked(
    zielmarktProduktChecked: ZielmarktProduktChecked,
  ): CheckoutAction<ZielmarktProduktChecked> {
    return {
      type: CheckoutActions.ZIELMARKT_PRODUKT_CHECKED,
      payload: zielmarktProduktChecked,
    };
  }

  zielmarktBegruendungChanged(
    zielmarktBegruendungChanged: ZielmarktBegruendungChanged,
  ): CheckoutAction<ZielmarktBegruendungChanged> {
    return {
      type: CheckoutActions.ZIELMARKT_BEGRUENDUNG_CHANGED,
      payload: zielmarktBegruendungChanged,
    };
  }

  zurueckZumShopModalClose(): CheckoutAction<void> {
    return { type: CheckoutActions.ZURUECK_ZUM_SHOP_MODAL_CLOSE };
  }

  zurueckZumShopModalOpen(): CheckoutAction<void> {
    return { type: CheckoutActions.ZURUECK_ZUM_SHOP_MODAL_OPEN };
  }

  kommunikationsdatenAnlegenRequested(): AnyAction {
    return { type: CheckoutActions.KOMMUNIKATIONSDATEN_ANLEGEN_REQUESTED };
  }

  jagdscheinChanged(jagdschein: JagdscheinChanged): CheckoutAction<JagdscheinChanged> {
    return {
      type: CheckoutActions.JAGDSCHEIN_CHANGED,
      payload: jagdschein,
    };
  }

  jagdbehoerdeNameChanged(behoerdename: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.JAGDBEHOERDE_NAME_CHANGED,
      payload: behoerdename,
    };
  }

  postleitzahlChangedJagdBehoerde(plz: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.JAGDBEHOERDE_PLZ_CHANGED,
      payload: plz,
    };
  }

  ortChangedJagdBehoerde(ort: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.JAGDBEHOERDE_ORT_CHANGED,
      payload: ort,
    };
  }

  strasseChangedJagdbehoerde(strasse: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.JAGDBEHOERDE_STRASSE_CHANGED,
      payload: strasse,
    };
  }

  hausnummerChangedJagdbehoerde(hausnummer: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.JAGDBEHOERDE_HAUSNUMMER_CHANGED,
      payload: hausnummer,
    };
  }

  maklermandatVorhandenChanged(
    maklermandatVorhanden: boolean,
  ): CheckoutAction<{ maklermandatVorhanden: boolean }> {
    return {
      type: CheckoutActions.MAKLERMANDAT_VORHANDEN_CHANGED,
      payload: { maklermandatVorhanden },
    };
  }

  fernabsatzChanged(fernabsatz: boolean | null): CheckoutAction<boolean | null> {
    return {
      type: CheckoutActions.FERNABSATZ_CHANGED,
      payload: fernabsatz,
    };
  }

  meldebehoerdeVorhandenChanged(meldebehoerdeVorhanden: boolean): CheckoutAction<boolean> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_VORHANDEN_CHANGED,
      payload: meldebehoerdeVorhanden,
    };
  }

  aktenzeichenVorhandenChanged(
    meldebehoerdeaktenzeichenVorhanden: boolean,
  ): CheckoutAction<boolean> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_AKTENZEICHEN_VORHANDEN_CHANGED,
      payload: meldebehoerdeaktenzeichenVorhanden,
    };
  }

  meldebehoerdeNameChanged(behoerdename: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_NAME_CHANGED,
      payload: behoerdename,
    };
  }

  postleitzahlChangedMeldeBehoerde(plz: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_PLZ_CHANGED,
      payload: plz,
    };
  }

  ortChangedMeldeBehoerde(ort: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_ORT_CHANGED,
      payload: ort,
    };
  }

  beginnsicherungmeldebehoerde(beginnsicherungsbestatigung: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_SICHERUNGSDATUM_CHANGED,
      payload: beginnsicherungsbestatigung,
    };
  }

  aktenzeichenChanged(aktenzeichen: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_AKTENZEICHEN_CHANGED,
      payload: aktenzeichen,
    };
  }

  strasseChangedMeldebehoerde(strasse: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_STRASSE_CHANGED,
      payload: strasse,
    };
  }

  hausnummerChangedMeldebehoerde(hausnummer: string): CheckoutAction<string> {
    return {
      type: CheckoutActions.MELDEBEHOERDE_HAUSNUMMER_CHANGED,
      payload: hausnummer,
    };
  }

  changePferdAngabe(
    changedValues: Partial<ThhPferdAngabenElement>,
    index: number,
  ): CheckoutAction<ThhPferdAngabeChangedPayload> {
    return {
      type: CheckoutActions.THH_PFERD_ANGABE_CHANGED,
      payload: {
        index,
        changedValues,
      },
    };
  }

  changeHundAngabe(
    changedValues: Partial<ThhHundAngabenElement>,
    index: number,
  ): CheckoutAction<ThhHundAngabeChangedPayload> {
    return {
      type: CheckoutActions.THH_HUND_ANGABE_CHANGED,
      payload: {
        index,
        changedValues,
      },
    };
  }

  changeHundKennzeichnung(kennzeichnung: HundKennzeichnung, index: number): AnyAction {
    return {
      type: CheckoutActions.THH_HUND_KENNZEICHENART_CHANGED,
      payload: { kennzeichnung, index },
    };
  }

  changePferdKennzeichnung(kennzeichnung: PferdKennzeichnung, index: number): AnyAction {
    return {
      type: CheckoutActions.THH_PFERD_KENNZEICHENART_CHANGED,
      payload: { kennzeichnung, index },
    };
  }

  staatsbuergerschaftenListeRequested(): AnyAction {
    return { type: CheckoutActions.STAATSBUERGERSCHAFTEN_REQUESTED };
  }

  diagnoseChanged(
    value: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<TkvDiagnosePayload> {
    return {
      type: CheckoutActions.DIAGNOSE_CHANGED,
      payload: { value, index, angebotsId },
    };
  }

  diagnoseDetailsChanged(
    value: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<TkvDiagnosePayload> {
    return {
      type: CheckoutActions.DIAGNOSE_DETAIL_CHANGED,
      payload: { value, index, angebotsId },
    };
  }

  diagnoseBeschreibungChanged(
    value: string,
    index: number,
    angebotsId: string,
  ): CheckoutAction<TkvDiagnosePayload> {
    return {
      type: CheckoutActions.DIAGNOSE_BESCHREIBUNG_CHANGED,
      payload: { value, index, angebotsId },
    };
  }

  tierarztBesuchtChanged(
    value: boolean,
    angebotsId: string,
  ): CheckoutAction<TkvTierarztBesuchtPayload> {
    return {
      type: CheckoutActions.TIERARZT_BESUCHT_CHANGED,
      payload: { value, angebotsId },
    };
  }

  tiernameChanged(value: string, angebotsId: string): CheckoutAction<TkvAngabenPayload> {
    return {
      type: CheckoutActions.TKV_TIERNAME_CHANGED,
      payload: { value, angebotsId },
    };
  }

  einsatzgebietChanged(
    value: EinsatzgebieteTier,
    angebotsId: string,
  ): CheckoutAction<TkvEinsatzgebietPayload> {
    return {
      type: CheckoutActions.TKV_EINSATZGEBIET_CHANGED,
      payload: { value, angebotsId },
    };
  }

  kennzeichnungsArtChanged(
    value: Kennzeichnungen,
    angebotsId: string,
  ): CheckoutAction<TkvKennzeichnungsArtPayload> {
    return {
      type: CheckoutActions.TKV_KENNZEICHNUNGSART_CHANGED,
      payload: { value, angebotsId },
    };
  }

  kennzeichnungChanged(value: string, angebotsId: string): CheckoutAction<TkvAngabenPayload> {
    return {
      type: CheckoutActions.TKV_KENNZEICHNUNG_CHANGED,
      payload: { value, angebotsId },
    };
  }

  assistanceDogChanged(
    value: boolean,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2BooleanPayload> {
    return {
      type: CheckoutActions.TKV_V2_ASSISTANCE_DOG_CHANGED,
      payload: { value, angebotsId },
    };
  }

  healthStatusListDogCatReceived(
    initialHealthStatusEntries: InitialHealthStatusEntry[],
  ): CheckoutAction<TkvAngabenV2HealthStatusListPayload> {
    return {
      type: CheckoutActions.TKV_V2_HEALTH_STATUS_LIST_DOG_CAT_RECEIVED,
      payload: { initialHealthStatusEntries },
    };
  }

  healthStatusListHorseReceived(
    initialHealthStatusEntries: InitialHealthStatusEntry[],
  ): CheckoutAction<TkvAngabenV2HealthStatusListPayload> {
    return {
      type: CheckoutActions.TKV_V2_HEALTH_STATUS_LIST_HORSE_RECEIVED,
      payload: { initialHealthStatusEntries },
    };
  }

  healthStatusEntryAdded(
    value: string,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2StringPayload> {
    return {
      type: CheckoutActions.TKV_V2_HEALTH_STATUS_ENTRY_ADDED,
      payload: { value, angebotsId },
    };
  }

  healthStatusEntryRemoved(
    value: string,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2StringPayload> {
    return {
      type: CheckoutActions.TKV_V2_HEALTH_STATUS_ENTRY_REMOVED,
      payload: { value, angebotsId },
    };
  }

  diagnosisChanged(
    id: string,
    diagnosis: string,
    animalType: Tierart,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2DiagnosisPayload> {
    return {
      type: CheckoutActions.TKV_V2_DIAGNOSIS_CHANGED,
      payload: { id, diagnosis, animalType, angebotsId },
    };
  }

  diagnosisDetailsChanged(
    id: string,
    selectedDetails: string[],
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2DiagnosisDetailsPayload> {
    return {
      type: CheckoutActions.TKV_V2_DIAGNOSIS_DETAILS_CHANGED,
      payload: { id, selectedDetails, angebotsId },
    };
  }

  diagnosisDescriptionChanged(
    id: string,
    description: string,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2DiagnosisDescriptionPayload> {
    return {
      type: CheckoutActions.TKV_V2_DIAGNOSIS_DESCRIPTION_CHANGED,
      payload: { id, description, angebotsId },
    };
  }

  treatmentStatusChanged(
    id: string,
    selectedDetail: string,
    isHealed: boolean,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2TreatmentStatusPayload> {
    return {
      type: CheckoutActions.TKV_V2_TREATMENT_STATUS_CHANGED,
      payload: { id, selectedDetail, isHealed, angebotsId },
    };
  }

  identificationTypeChanged(
    value: KennzeichnungsArt,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2IdentificationTypePayload> {
    return {
      type: CheckoutActions.TKV_V2_IDENTIFICATION_TYPE_CHANGED,
      payload: { value, angebotsId },
    };
  }

  identificationChanged(
    value: string,
    angebotsId: string,
  ): CheckoutAction<TkvAngabenV2StringPayload> {
    return {
      type: CheckoutActions.TKV_V2_IDENTIFICATION_CHANGED,
      payload: { value, angebotsId },
    };
  }

  changeAzamAbweichenderKontoinhaberPlz(plz: string): CheckoutAction<PLZ> {
    return {
      type: CheckoutActions.AZAM_ABWEICHENDER_KONTOINHABER_PLZ_CHANGED,
      payload: { plz },
    };
  }

  changeAzamBezugsberechtigterPlz(plz: string): CheckoutAction<PLZ> {
    return {
      type: CheckoutActions.AZAM_BEZUGSBERECHTIGTER_PLZ_CHANGED,
      payload: { plz },
    };
  }

  partialUpdate(changes: Partial<Kontoinhaber>): CheckoutAction<Partial<Kontoinhaber>> {
    return {
      type: CheckoutActions.AZAM_PARTIAL_KONTOINHABER_UPDATE,
      payload: changes,
    };
  }

  partialBezugsberechtigenUpdate(
    changes: Partial<BezugsberechtigterModal>,
  ): CheckoutAction<Partial<BezugsberechtigterModal>> {
    return {
      type: CheckoutActions.AZAM_PARTIAL_BEZUGSBERECHTIGER_UPDATE,
      payload: changes,
    };
  }

  changeAzamAbweichenderKontoinhaberAdresse(
    adressAenderung: Partial<Adresse>,
  ): CheckoutAction<Partial<Adresse>> {
    return {
      type: CheckoutActions.AZAM_PARTIAL_KONTOINHABER_ADDRESS_UPDATE,
      payload: adressAenderung,
    };
  }

  changeAzamBezugsberechtigterAdresse(
    adressAenderung: Partial<Adresse>,
  ): CheckoutAction<Partial<Adresse>> {
    return {
      type: CheckoutActions.AZAM_PARTIAL_BEZUGSBERECHTIGTER_ADDRESS_UPDATE,
      payload: adressAenderung,
    };
  }

  sortProdukte(angeboteInWarenkord: Array<AngebotsId>): CheckoutAction<Array<AngebotsId>> {
    return {
      type: CheckoutActions.SORT_PRODUKTE,
      payload: angeboteInWarenkord,
    };
  }

  vnWechselgrundListReceived(vnWechselgrundList: Wechselgrund[]): CheckoutAction<Wechselgrund[]> {
    return {
      type: CheckoutActions.VN_WECHSELGRUND_LIST_RECEIVED,
      payload: vnWechselgrundList,
    };
  }

  ausschlussListReceived(ausschlussList: string[]): CheckoutAction<string[]> {
    return {
      type: CheckoutActions.AUSSCHLUSS_LIST_RECEIVED,
      payload: ausschlussList,
    };
  }

  assekuranzTarifChanged(assekuranzTarif: boolean): CheckoutAction<AssekuranzTarif> {
    return {
      type: CheckoutActions.BERATUNG_ASSEKURANZ_TARIF_CHANGED,
      payload: {
        assekuranzTarif,
      },
    };
  }

  unfDynamikChanged(payload: UnfDynamikChangedPayload): CheckoutAction<UnfDynamikChangedPayload> {
    return {
      type: CheckoutActions.UNF_DYNAMIK_CHANGED,
      payload,
    };
  }

  unfBezugsrechtChanged(
    payload: UnfBezugsrechtChangedPayload,
  ): CheckoutAction<UnfBezugsrechtChangedPayload> {
    return {
      type: CheckoutActions.UNF_BEZUGSRECHT_CHANGED,
      payload,
    };
  }

  unfKisBeeintraechtigunenChanged(
    payload: UnfKisBeeintraechtigungenPayload,
  ): CheckoutAction<UnfKisBeeintraechtigungenPayload> {
    return {
      type: CheckoutActions.UNF_KIS_BEEINTRAECHTIGUNGEN_CHANGED,
      payload,
    };
  }

  unfIsSofortschutzBesprochenChanged(
    payload: UnfIsSofortschutzBesprochenPayload,
  ): CheckoutAction<UnfIsSofortschutzBesprochenPayload> {
    return {
      type: CheckoutActions.UNF_IS_SOFORTSCHUTZ_BESPROCHEN_CHANGED,
      payload,
    };
  }

  unfKisFrageChanged(payload: UnfKisFrageJaNeinChanged): CheckoutAction<UnfKisFrageJaNeinChanged> {
    return {
      type: CheckoutActions.UNF_KIS_FRAGE_CHANGED,
      payload,
    };
  }

  unfUnterlagenWerdenEingereichtChanged(
    payload: UnfKisUnterlageneChangedPayload,
  ): CheckoutAction<UnfKisUnterlageneChangedPayload> {
    return {
      type: CheckoutActions.UNF_KIS_UNTERLAGENEINGEREICHT_CHANGED,
      payload,
    };
  }

  unfDatenschutzEinwilligungChanged(payload: boolean): CheckoutAction<boolean> {
    return {
      type: CheckoutActions.UNF_DATENSCHUTZEINWILLIGUNG_CHANGED,
      payload,
    };
  }

  unfPflegeFrageChanged(
    payload: UnfPflegeFrageChangedPayload,
  ): CheckoutAction<UnfPflegeFrageChangedPayload> {
    return {
      type: CheckoutActions.UNF_PFLEGEFRAGE_CHANGED,
      payload,
    };
  }

  unfAddBezugsberechtigter(
    payload: UnfAddBezugsberechtigterPayload,
  ): CheckoutAction<UnfAddBezugsberechtigterPayload> {
    return {
      type: CheckoutActions.UNF_ADD_BEZUGSBERECHTIGTER,
      payload,
    };
  }

  unfEditBezugsberechtigter(
    payload: UnfEditBezugsberechtigterPayload,
  ): CheckoutAction<UnfEditBezugsberechtigterPayload> {
    return {
      type: CheckoutActions.UNF_EDIT_BEZUGSBERECHTIGTER,
      payload,
    };
  }

  unfDeleteBezugsberechtigter(
    payload: UnfDeleteBezugsberechtigterPayload,
  ): CheckoutAction<UnfDeleteBezugsberechtigterPayload> {
    return {
      type: CheckoutActions.UNF_DELETE_BEZUGSBERECHTIGTER,
      payload,
    };
  }

  updateGesundheitsdaten(payload: SavedState): CheckoutAction<SavedState> {
    return {
      type: CheckoutActions.UNF_UPDATE_GESUNDHEITSDATEN_AFTER_LOAD,
      payload,
    };
  }

  vhvWertsachenSummenChanged(
    angebotsId: string,
    wertsachen: Wertsachen,
  ): CheckoutAction<{ angebotsId: string; wertsachen: Wertsachen }> {
    return {
      type: CheckoutActions.VHV_WERTSACHEN_SUMMEN_CHANGED,
      payload: {
        angebotsId,
        wertsachen,
      },
    };
  }

  vhvModeaccessoiresChanged(
    angebotsId: string,
    modeaccessoires: Modeaccessoires,
  ): CheckoutAction<{ angebotsId: string; modeaccessoires: Modeaccessoires }> {
    return {
      type: CheckoutActions.VHV_MODEACCESSOIRES_CHANGED,
      payload: {
        angebotsId,
        modeaccessoires,
      },
    };
  }

  tresoreVorhandenChanged(
    tresoreVorhanden: boolean,
    angebotsId: string,
  ): CheckoutAction<{
    angebotsId: string;
    tresoreVorhanden: boolean;
  }> {
    return {
      type: CheckoutActions.TRESORE_VORHANDEN_CHANGED,
      payload: {
        tresoreVorhanden,
        angebotsId,
      },
    };
  }

  einbruchmeldeanlageVorhandenChanged(
    emaVorhanden: boolean,
    angebotsId: string,
  ): CheckoutAction<{
    angebotsId: string;
    emaVorhanden: boolean;
  }> {
    return {
      type: CheckoutActions.EMA_VORHANDEN_CHANGED,
      payload: {
        emaVorhanden,
        angebotsId,
      },
    };
  }

  einbruchMeldeanlageModalOpenend(angebotsId: string): CheckoutAction<HaushaltModalPayload> {
    return {
      type: CheckoutActions.EINBRUCHMELDEANLAGE_MODAL_OPENED,
      payload: { angebotsId },
    };
  }

  wertebehaeltnisModalOpenend(angebotsId: string): CheckoutAction<HaushaltModalPayload> {
    return {
      type: CheckoutActions.WERTBEHAELTNISSE_MODAL_OPENED,
      payload: { angebotsId },
    };
  }

  mechanischeSicherungenModalOpened(angebotsId: string): CheckoutAction<HaushaltModalPayload> {
    return {
      type: CheckoutActions.MECHANISCHE_SICHERUNGEN_MODAL_OPENED,
      payload: { angebotsId },
    };
  }

  fragebogenValidationRevealed(
    angebotsId: string,
    fragebogenType: FragebogenType,
  ): CheckoutAction<FragebogenValidationRevealedPayload> {
    return {
      type: CheckoutActions.FRAGEBOGEN_VALIDATION_REVEALED,
      payload: { angebotsId, fragebogenType },
    };
  }

  einbruchMeldeanlageChanged(
    einbruchMeldeanlage: EinbruchMeldeanlage,
    angebotsId: string,
    isFormValid: boolean,
  ): CheckoutAction<EinbruchMeldeanlageChangedPayload> {
    return {
      type: CheckoutActions.EMA_CHANGED,
      payload: { einbruchMeldeanlage, angebotsId, isFormValid },
    };
  }

  tresorChanged(
    tresorEintraege: TresorEintrag[],
    angebotsId: string,
    isFormValid: boolean,
  ): CheckoutAction<TresorChangedPayload> {
    return {
      type: CheckoutActions.TRESOR_CHANGED,
      payload: { tresorEintraege, angebotsId, isFormValid },
    };
  }

  mechanischeSicherungenChanged(
    mechanischeSicherungen: MechanischeSicherungen,
    angebotsId: string,
    isFormValid: boolean,
  ): CheckoutAction<MechanischeSicherungenChangedPayload> {
    return {
      type: CheckoutActions.MECHANISCHE_SICHERUNGEN_CHANGED,
      payload: { mechanischeSicherungen, angebotsId, isFormValid },
    };
  }

  deleteFile(
    documentId: string,
    fileUploaderId: string,
    angebotsId: string,
  ): CheckoutAction<FileToDeletePayload> {
    return {
      type: CheckoutActions.FILE_DELETION_REQUESTED,
      payload: { documentId, fileUploaderId, angebotsId },
    };
  }

  detectNotExistingFiles(documentIds: Array<string>): CheckoutAction<Array<string>> {
    return {
      type: CheckoutActions.REMOVE_NOT_EXISTING_FILES,
      payload: documentIds,
    };
  }

  markFileToDelete(
    documentId: string,
    fileUploaderId: string,
    angebotsId: string,
  ): CheckoutAction<FileToDeletePayload> {
    return {
      type: CheckoutActions.MARK_FILE_TO_DELETE,
      payload: { documentId, fileUploaderId, angebotsId },
    };
  }

  cleanupFilesToDelete(angebotsId: string): CheckoutAction<{ angebotsId: string }> {
    return {
      type: CheckoutActions.CLEANUP_FILES_TO_DELETE,
      payload: { angebotsId },
    };
  }

  closeFileDeletionMessage(fileUploaderId: string): CheckoutAction<{ fileUploaderId: string }> {
    return {
      type: CheckoutActions.CLOSE_FILE_DELETION_MESSAGES,
      payload: {
        fileUploaderId: fileUploaderId,
      },
    };
  }

  fileDeletionFailed(fileUploaderId: string): CheckoutAction<{ fileUploaderId: string }> {
    return {
      type: CheckoutActions.FILE_DELETION_FAILED,
      payload: {
        fileUploaderId: fileUploaderId,
      },
    };
  }

  fileDeleted(
    documentId: string,
    fileUploaderId: string,
    angebotsId: string,
  ): CheckoutAction<FileToDeletePayload> {
    return {
      type: CheckoutActions.FILE_DELETED,
      payload: {
        documentId: documentId,
        fileUploaderId: fileUploaderId,
        angebotsId,
      },
    };
  }

  validiereComplectionCard(): AnyAction {
    return { type: CheckoutActions.VALIDIERE_INPUT_COMPLECTION_CARD };
  }

  elementarFragebogenfilesSuccessfullyUploaded(
    filesIds: string[],
  ): CheckoutAction<ElementarfragebogenFilesUploadedSuccessfully> {
    return {
      type: CheckoutActions.ELEMENTARFRAGEBOGEN_FILES_UPLOADED_SUCCESSFULLY,
      filesIds,
    };
  }

  elementarfragebogenUploadedFilesChanged(
    angebotsId: string,
    event: UploadedFile[],
  ): CheckoutAction<ElementarfragebogenUploadedFilesChange> {
    return {
      type: CheckoutActions.ELEMENTARFRAGEBOGEN_UPLOADED_FILES_CHANGED,
      payload: {
        angebotsId,
        uploadedfiles: event,
      },
    };
  }

  elementarFragebogenfilesSuccessfullyDeleted(
    extractedDocumentId: string,
    angebotsId: string,
  ): CheckoutAction<{ deletedFileId: string; angebotsId: string }> {
    return {
      type: CheckoutActions.ELEMENTARFRAGEBOGEN_FILE_DELETED_SUCCESSFULLY,
      payload: { deletedFileId: extractedDocumentId, angebotsId: angebotsId },
    };
  }

  removeElementarfragebogenFilesSuccessfullyOperationStatus(): CheckoutAction<void> {
    return { type: CheckoutActions.REMOVE_ELEMENTARFRAGEBOGEN_OPERATIONS_STATUS };
  }
}

const _checkoutActions = new CheckoutActions();
export type UnfDynamikChangedAction = ReturnType<typeof _checkoutActions.unfDynamikChanged>;
export type UnfBezugsrechtChangedAction = ReturnType<typeof _checkoutActions.unfBezugsrechtChanged>;
export type UnfDatenschutzEinwilligungChangedAction = ReturnType<
  typeof _checkoutActions.unfDatenschutzEinwilligungChanged
>;
export type UnfPflegeFrageChangedAction = ReturnType<typeof _checkoutActions.unfPflegeFrageChanged>;
export type UnfKisFrageJaNeinChangedAction = ReturnType<typeof _checkoutActions.unfKisFrageChanged>;
export type UnfKisBeeintraechtigungenAction = ReturnType<
  typeof _checkoutActions.unfKisBeeintraechtigunenChanged
>;
export type UnfallhilfeSofortBesprochenAction = ReturnType<
  typeof _checkoutActions.unfIsSofortschutzBesprochenChanged
>;
export type UnfKisUnterlagenWerdenEingereichtAction = ReturnType<
  typeof _checkoutActions.unfUnterlagenWerdenEingereichtChanged
>;
export type UnfAddBezugsberechtigterAction = ReturnType<
  typeof _checkoutActions.unfAddBezugsberechtigter
>;
export type UnfDeleteBezugsberechtigterAction = ReturnType<
  typeof _checkoutActions.unfDeleteBezugsberechtigter
>;
